import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CommonServiceService } from 'src/app/shared/common-service.service';
import { environment } from 'src/environments/environment';
import * as moment from 'moment-timezone';
import { OpenAdvanceSearchModal } from 'src/app/transactions/store/actions';
import { Store } from '@ngrx/store';
import { InitialState } from 'src/app/store/reducer';

@Component({
  selector: 'app-audit-trail-list',
  templateUrl: './audit-trail-list.component.html',
  styleUrls: ['./audit-trail-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuditTrailListComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();

  auditTrailArr: any[] = [];
  onSearch = '';
  duration: any = {};

  pageNumber = 1;
  pageSize = 50;
  totalRecords = 0;

  startDate!: Date;
  endDate!: Date;

  onSearchFilterChanged = new Subject<string>();
  queryParams: any = {};

  navigationSubscription: any;
  reInitialise = false;

  // Infinite Scroll Properties
  innerHeight = (window.innerHeight - 220) + 'px';
  selector = '.table-container';
  currentPage: number = 1;
  isLoading: boolean = false;

  selectedDiff: any = null;
  diffLoading = false;
  isDiffModalVisible = false;

  historyArr: any[] = [];
  historyLoading = false;
  isHistoryModalVisible = false;

  selectedHistoryRow: any = null;
  selectedDiffRow: any = null;

  constructor(
    public commonService: CommonServiceService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<InitialState>,
    private cdr: ChangeDetectorRef
  ) {
    this.navigationSubscription = this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((e: any) => {
        if (e instanceof NavigationEnd) {
          this.initialisePage();
        }
      });
  }

  ngOnInit(): void {

    const params = this.route.snapshot.queryParams || {};
    this.queryParams = params;

    if (params.start_date && params.end_date) {
      this.startDate = moment(params.start_date, 'YYYY-MM-DD').toDate();
      this.endDate = moment(params.end_date, 'YYYY-MM-DD').toDate();
    } else {
      this.initializeDateRange();
    }

    this.loadAuditTrail();
    this.reInitialise = true;

    this.onSearchFilterChanged
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: string) => {
        this.onSearch = value;
        this.currentPage = 1;
        this.auditTrailArr = [];
        this.loadAuditTrail(false);
      });
  }

  initialisePage(): void {
    if (this.reInitialise) {
      this.ngOnInit();
    }
  }

  initializeDateRange(): void {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // getMonth() returns 0-11

    // Financial year starts in April (month 4)
    if (currentMonth >= 4) {
      // Current financial year: April currentYear to March nextYear
      this.startDate = new Date(currentYear, 3, 1); // April 1st of current year
    } else {
      // Previous financial year: April previousYear to March currentYear
      this.startDate = new Date(currentYear - 1, 3, 1); // April 1st of previous year
    }

    this.endDate = new Date(); // Today
  }

  formatDate(date: Date): string {
    return moment(date).format('DD/MM/YYYY');
  }

  formatDateForAPI(date: Date): string {
    return moment(date).format('YYYY-MM-DD HH:mm:ss');
  }

  loadAuditTrail(isScrollLoad: boolean = false): void {
    // Prevent duplicate requests during loading
    if (this.isLoading) return;

    this.isLoading = true;


    const params = {
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      fromDate: moment(this.startDate).format('YYYY-MM-DD 00:00:00'),
      toDate: moment(this.endDate).format('YYYY-MM-DD 23:59:59'),
      search: this.onSearch,
      ...this.queryParams
    };

    console.log('Loading audit trail with params:', params);

    this.commonService
      .getRequestWithQueryParams(
        `${environment.okountUrl}/audit-trail`,
        params
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          console.log('Audit trail response:', res);
          this.isLoading = false;


          const newData = res.data || [];

          if (isScrollLoad) {
            // Append data for infinite scroll
            console.log('Appending', newData.length, 'records');
            this.auditTrailArr = this.auditTrailArr.concat(newData);
            if (newData.length > 0) {  // ✅ Only increment if data came back
              this.currentPage++;
            }
          } else {
            // Replace entire data for initial load or search
            console.log('Initial load with', newData.length, 'records');
            this.auditTrailArr = newData;
            this.currentPage = 2;
          }

          this.totalRecords = res.totalRecords || 0;
          this.pageSize = res.pageSize || 50;

          this.duration = res.duration || {
            from: moment(this.startDate).format('DD/MM/YYYY'),
            to: moment(this.endDate).format('DD/MM/YYYY')
          };

          console.log('Total records:', this.totalRecords, 'Current array length:', this.auditTrailArr.length);
          this.setHeight(window.innerHeight);
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.isLoading = false;

          if (!isScrollLoad) {
            this.auditTrailArr = [];
          }
          console.error('Failed to load audit trail', error);
          this.cdr.markForCheck();
        }
      );
  }

  onSearchFilter(value: string): void {
    this.onSearchFilterChanged.next(value);
  }

  // INFINITE SCROLL METHODS
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.innerHeight = (window.innerHeight - 220) + 'px';
    this.cdr.markForCheck();
  }

  setHeight(height: number): void {
    if (this.auditTrailArr != undefined) {
      if (height == undefined) {
        height = window.innerHeight;
      }
      if (this.auditTrailArr.length <= 9) {
        this.innerHeight = 'auto';
      } else {
        this.innerHeight = 'auto';
      }
    }
  }

  onScrollDown(event: any): void {
    console.log('Scrolled Down - Loading more records');
    // Only load more if we haven't loaded all records and no load in progress
    if (this.auditTrailArr.length < this.totalRecords && !this.isLoading) {

      this.loadAuditTrail(true);
    }
  }

  onTableScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 150;
    const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - threshold;

    if (isNearBottom) {
      this.onScrollDown(event);
    }
  }

  onScrollUp(event: any): void {
    console.log('Scrolled Up');
  }

  downloadExcel(): void {
    // if (this.isLoading) return;


    const params = {
      fromDate: moment(this.startDate).format('YYYY-MM-DD HH:mm:ss'),
      toDate: moment(this.endDate).format('YYYY-MM-DD HH:mm:ss'),
      search: this.onSearch || '',
      ...this.queryParams
    };

    this.commonService
      .getRequestWithPdfData(
        `${environment.okountUrl}/audit-trail/export`,
        params
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (blob: Blob) => {

          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `audit-trail-${moment().format('YYYYMMDDHHmmss')}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => window.URL.revokeObjectURL(url), 1000);
          this.cdr.markForCheck();
        },
        (error: any) => {

          console.error('Excel download failed', error);
          this.cdr.markForCheck();
        }
      );
  }

  formatDateTime(dateString: string): string {
    return moment(dateString).format('DD/MM/YYYY HH:mm:ss');
  }

  openAdvanceSearchModal() {
    this.store.dispatch(new OpenAdvanceSearchModal({ status: true, title: 'client' }));
  }

  getActionColor(action: string): string {
    switch ((action || '').toUpperCase()) {
      case 'CREATE':
      case 'INSERT':
        return 'green';

      case 'UPDATE':
      case 'EDIT':
        return 'blue';

      case 'DELETE':
        return 'red';

      default:
        return 'gray';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  viewDiff(row: any): void {
    console.log('View diff for row:', row);
    if (row.action_type !== 'UPDATE') return;
 
    this.selectedDiffRow = row;
    this.isDiffModalVisible = true;
    this.diffLoading = true;
    this.selectedDiff = null;
    this.cdr.markForCheck();
 
    const params = {
      table_name: row.table_name,
      record_id: row.id
    };
 
    console.log('parameters for diff API:', params);
 
    this.commonService
      .getRequestWithQueryParamsNoLoader(
        `${environment.okountUrl}/audit-trail/diff`,
        params
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          console.log('DIFF RESPONSE', res);
          this.diffLoading = false;
 
          const allDiffs: any[] = res.diff || [];
 
          // The API returns diffs for all consecutive version pairs.
          // Each row has a seq_no (1-based). The diff for a specific row is
          // the transition that ends at that row's seq_no, i.e.:
          //   seq_no 2  →  allDiffs[0]  (from_index 0 → to_index 1)
          //   seq_no 3  →  allDiffs[1]  (from_index 1 → to_index 2)
          //   etc.
          // So the index into allDiffs = seq_no - 2
          const seqNo = Number(row.seq_no);
          const diffIndex = seqNo - 2;
 
          const matchedDiff = allDiffs[diffIndex];
 
          // Fall back to the last diff if index is out of range
          this.selectedDiff = matchedDiff
            ? matchedDiff.changes
            : (allDiffs.length > 0 ? allDiffs[allDiffs.length - 1].changes : null);
 
          console.log('SELECTED DIFF', this.selectedDiff);
          this.isDiffModalVisible = true;
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.diffLoading = false;
          console.error('Failed to load diff', error);
          this.cdr.markForCheck();
        }
      );
  }

  closeDiffModal(): void {
    this.isDiffModalVisible = false;
    this.selectedDiff = null;
    this.selectedDiffRow = null;
    this.cdr.markForCheck();
  }

   hasDiffChanges(diff: any): boolean {
    if (!diff) return false;
 
    // diff is now a single changes object (already extracted above), not an array
    return (
      (diff.top_level_changes && diff.top_level_changes.length > 0) ||
      (diff.total_fare_changes && diff.total_fare_changes.length > 0) ||
      (diff.traveller_changes && diff.traveller_changes.length > 0) ||
      (diff.sector_changes && diff.sector_changes.length > 0)
    );
  }

  viewHistory(row: any): void {
    this.selectedHistoryRow = row;
    this.historyArr = [];
    this.historyLoading = true;
    this.isHistoryModalVisible = true;
    this.cdr.markForCheck();

    const params = {
      transaction_no: row.transaction_no,
      table_name: row.table_name
    };

    this.commonService
      .getRequestWithQueryParamsNoLoader(
        `${environment.okountUrl}/audit-trail/history`,
        params
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.historyArr = res.data || [];
          this.historyLoading = false;
          this.cdr.markForCheck();
        },
        (error: any) => {
          console.error(error);
          this.historyLoading = false;
          this.cdr.markForCheck();
        }
      );
  }


  closeHistoryModal(): void {
    this.isHistoryModalVisible = false;
    this.historyArr = [];
    this.selectedHistoryRow = null;
    this.cdr.markForCheck();
  }

}
